import React, { useState, useEffect } from "react";
import { Tabs, Card, Spin, Row, Table, Col } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getCorporateDemographyReport, fetchEnrollmentPolicyType } from "../../Action/Actions";
// import { useSelectedYear } from "../../Context/SelectYearContext"; // --- REMOVED
import { selectFinancialYears, selectSelectedYearId } from "../../Redux/financialYearSlice"; // +++ ADDED
import { getUserData } from "../login/LoginSlice";
import "./Report.css";
import { TeamOutlined, UserOutlined, FileAddOutlined, FireOutlined } from "@ant-design/icons";

const RESTRICTED_POLICY_TYPE_IDS = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];

const DemographyReport = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { module_option_ids } = location.state || {};
  const AuthData = useSelector(getUserData);

  // +++ ADDED: Get financial year data from Redux store
  const selectedYearId = useSelector(selectSelectedYearId);
  const financialYears = useSelector(selectFinancialYears);
  
  // Find the selected year's display name from the year list
  const selectedYear = financialYears.find(year => year.id === selectedYearId)?.financial_year || "Not Selected";

  const [activeTab, setActiveTab] = useState("");
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  // const { selectedYear } = useSelectedYear(); // --- REMOVED
  const [policyTypeData, setPolicyTypeData] = useState({});
  const [loading, setLoading] = useState(false);
  const [demographyData, setDemographyData] = useState({});
  const user = useSelector(getUserData);
  const policyTypes = Object.keys(policyTypeData || {});

  useEffect(() => {
    const fetchPolicyData = async () => {
      if (!AuthData?.corporate_id || !selectedYearId) {
        setLoading(false);
        setPolicyTypeData({});
        setSelectedPolicy(null);
        return;
      }
      try {
        setLoading(true);
        const response = await dispatch(fetchEnrollmentPolicyType(AuthData?.corporate_id));
        const policyTypeList = response?.policy_type_list || {};
        
        // NEW: Filter out restricted policy types
        const filtered_policyTypeList = {};
        for (const type in policyTypeList) {
          const typeName = String(type).toLowerCase();
          if (typeName.includes('marine') || typeName.includes('fire')) {
            continue;
          }

          const policies = policyTypeList[type] || [];
          const validPolicies = policies.filter(p => {
            const details = Array.isArray(p.policy_details) ? p.policy_details[0] : p.policy_details;
            const typeId = details?.policy_type_id || p.policy_type_id || p.policy_details?.policy_type_id;
            const isRestrictedId = RESTRICTED_POLICY_TYPE_IDS.includes(Number(typeId));
            return !isRestrictedId;
          });
          if (validPolicies.length > 0) {
            filtered_policyTypeList[type] = validPolicies;
          }
        }

        setPolicyTypeData(filtered_policyTypeList);
        
        // Get policy types that actually have data (not empty arrays)
        const availableTabs = Object.keys(filtered_policyTypeList || {}).filter(
          (tab) => filtered_policyTypeList[tab] && filtered_policyTypeList[tab].length > 0
        );
        
        // Use the first available type that has data
        const tabToUse = availableTabs.length > 0 ? availableTabs[0] : "";
        
        // Update activeTab to the first available type with data
        if (tabToUse && tabToUse !== activeTab) {
          setActiveTab(tabToUse);
        }
        
        // Reset to the first policy of the current tab when data reloads
        const firstPolicy = policyTypeList[tabToUse]?.[0];
        setSelectedPolicy(firstPolicy || null);
      } catch (err) {
        console.error("Error fetching policy type:", err);
        setPolicyTypeData({});
        setSelectedPolicy(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPolicyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, AuthData?.corporate_id, selectedYearId]);

  useEffect(() => {
    // Clear previous data if no policy is selected
    if (!selectedPolicy) {
      setDemographyData({});
      return;
    }

    if (selectedPolicy && selectedYearId) {
      setLoading(true);
      // This action now implicitly uses the financial year ID from Redux state
      dispatch(getCorporateDemographyReport(selectedPolicy.policy_id))
        .then((data) => {
          setDemographyData(data);
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          console.error("Error fetching demography report:", err);
        });
    }
    // +++ ADDED selectedYearId to dependency array
  }, [dispatch, selectedPolicy, selectedYearId]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    // When changing main tabs, select the first policy of that type
    const policy = policyTypeData[key]?.[0];
    setSelectedPolicy(policy || null);
  };

  const handlePolicyNumberClick = (key) => {
    // Find the policy object based on the key (either policy_number or policy-${index})
    const policy = policyTypeData[activeTab]?.find((p, index) => {
      const policyKey = p.policy_number || `policy-${index}`;
      return policyKey === key;
    });
    
    if (policy) {
      setSelectedPolicy(policy);
    }
  };
  
  const getTabIcon = (type) => {
    switch (type) {
      case "GMC":
        return <TeamOutlined style={{ marginRight: 8 }} />;
      case "GTL":
        return <UserOutlined style={{ marginRight: 8 }} />;
      case "GPA":
        return <TeamOutlined style={{ marginRight: 8 }} />;
      case "Parent policy":
        return <TeamOutlined style={{ marginRight: 8 }} />;
      case "Topup":
        return <FileAddOutlined style={{ marginRight: 8 }} />;
      case "Fire":
        return <FireOutlined style={{ marginRight: 8 }} />;
      default:
        return null;
    }
  };

  const demographyColumns = [
    { title: "SUM INSURED", dataIndex: "sumInsured", key: "sumInsured" },
    { title: "EMPLOYEE", dataIndex: "EMPLOYEE", key: "EMPLOYEE" },
    { title: "SPOUSE", dataIndex: "SPOUSE", key: "SPOUSE" },
    { title: "CHILDREN", dataIndex: "CHILDREN", key: "CHILDREN" },
    { title: "PARENTS", dataIndex: "PARENTS", key: "PARENTS" },
    { title: "TOTAL", dataIndex: "TOTAL", key: "TOTAL" },
  ];

  const totalLivesColumns = [
    { title: "LIVES", dataIndex: "Lives", key: "Lives" },
    { title: "TOTAL", dataIndex: "total", key: "total" },
  ];

  const noOfLivesColumns = [
    { title: "NO OF LIVES", dataIndex: "noOfLives", key: "noOfLives" },
    { title: "TOTAL", dataIndex: "total", key: "total" },
  ];

  const EscalationLevels = () => (
    <Card>
      <Row gutter={16}>
        <Col span={24} className="claim-card">
          <Table
            title={() => <div className="table-title">Demography Analysis</div>}
            columns={demographyColumns}
            dataSource={Object.entries(demographyData.Demography_analysis || {}).map(([key, value]) => ({
              sumInsured: key,
              ...value,
            }))}
            pagination={false}
            bordered
            rowClassName={(record) => (record.key === "2" ? "highlighted-total-row" : "")}
          />
        </Col>
        <Col span={12} className="claim-card">
          <Table
            title={() => <div className="table-title">Total Lives</div>}
            columns={totalLivesColumns}
            dataSource={Object.entries(demographyData.Total_lives || {}).map(([key, value]) => ({
              Lives: key,
              total: value,
            }))}
            pagination={false}
            bordered
            rowClassName={(record) => (record.key === "2" ? "highlighted-total-row" : "")}
          />
        </Col>
        <Col span={12} className="claim-card">
          <Table
            title={() => <div className="table-title">No of Lives</div>}
            columns={noOfLivesColumns}
            dataSource={Object.entries(demographyData.No_of_lives || {}).map(([key, value]) => ({
              noOfLives: key,
              total: value,
            }))}
            pagination={false}
            bordered
            rowClassName={(record) => (record.key === "2" ? "highlighted-total-row" : "")}
          />
        </Col>
      </Row>
    </Card>
  );

  const tabItems = Object.keys(policyTypeData || {}).map((type) => ({
    key: type,
    label: (
      <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
        {type}
        {getTabIcon(type)}
      </span>
    ),
    children: (
      <>
        <Tabs
          activeKey={selectedPolicy ? (selectedPolicy.policy_number || `policy-${policyTypeData[type]?.findIndex(p => p.policy_id === selectedPolicy.policy_id)}`) : undefined}
          onChange={handlePolicyNumberClick}
          items={(policyTypeData[type] || []).map((policy, index) => ({
            key: policy.policy_number || `policy-${index}`,
            label: policy.policy_number ? (
              policy.policy_number
            ) : (
              <span
                style={{
                  backgroundColor: "#FFFF00",
                  color: "#000000E0",
                  padding: "2px 8px",
                  fontSize: "12px",
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  borderRadius: "4px",
                  display: "inline-block",
                }}
              >
                Policy Number not issued
              </span>
            ),
          }))}
        />
        {selectedPolicy ? <EscalationLevels /> : null}
      </>
    ),
  }));

  return (
    <Spin spinning={loading}>
      <div className="escalation-matrix-container">
        <div className="escalation-content">
          <Row className="escalation-row-header">
            <span style={{ fontWeight: "700", fontSize: "20px" }}>
              {user?.corporate_name || "Guest"}
            </span>{" "}
            <span style={{ marginLeft: "8px" }}>
              {/* This now uses the 'selectedYear' variable derived from Redux state */}
              (Financial Year: {selectedYear})
            </span>
          </Row>
        </div>
        {policyTypes.length > 0 ? (
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            className="esc-tab"
            items={tabItems}
          />
        ) : (
          <Card style={{ marginTop: "20px" }}>
            <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
              <p style={{ fontSize: "16px", fontWeight: "500" }}>No demography report data available for {selectedYear}</p>
            </div>
          </Card>
        )}
      </div>
    </Spin>
  );
};

export default DemographyReport;